// productData.js
import axios from 'axios';
import { isFastTrackProduct } from './fastTrackAddon';
import { findAllInCatalog } from './catalogIndex';

// Configure axios instance with base URL
const api = axios.create({
    baseURL: process.env.VUE_APP_API_ENDPOINT
});

let cachedProducts = null;
let isFetching = false;
// Shared in-flight fetch promise. App.vue prefetches products on app init at the
// same time a checkout page mounts and asks for a product; concurrent callers must
// await THIS promise rather than getting the still-empty cache back.
let productFetchPromise = null;

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
const CACHE_KEY = 'cached_products';
const CACHE_TIMESTAMP_KEY = 'cached_products_timestamp';

export const initializeProducts = (products) => {
    // Only set fallback products if no products are already loaded
    // This prevents overwriting full product list from localStorage/server
    if (!cachedProducts || cachedProducts.length === 0) {
        cachedProducts = products;
    }
};

export const getProducts = () => {
    return cachedProducts;
};

export const createUrlName = (product) => {
    const baseName = product.name.toLowerCase();
    const variant = product.variant ? `-${product.variant}` : '';
    return `${baseName}${variant}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .replace(/--+/g, '-');
};

/**
 * Check if cached data is still valid (less than 1 hour old)
 */
const isCacheValid = () => {
    try {
        const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
        if (!timestamp) return false;

        const cacheAge = Date.now() - parseInt(timestamp, 10);
        return cacheAge < CACHE_DURATION;
    } catch (error) {
        console.error('Error checking cache validity:', error);
        return false;
    }
};

/**
 * Load products from localStorage cache
 */
export const loadCachedProducts = () => {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached && isCacheValid()) {
            const temp = JSON.parse(cached);
            cachedProducts = temp.products || temp;
            return true;
        }
    } catch (error) {
        console.error('Error loading cached products:', error);
    }
    return false;
};

/**
 * Fetch products from server and cache them
 * Only makes one request per hour
 */
export const fetchProductsFromServer = async (forceRefresh = false) => {
    // A request is already in flight — await the SAME promise instead of returning
    // the (possibly still-empty) cache. Prevents the App.vue-prefetch race where a
    // checkout mount would otherwise receive [] and fail to build its cart/plan.
    if (isFetching && productFetchPromise) {
        return productFetchPromise;
    }

    // Check if we have a complete cache (server-fetched products have timestamp key)
    const hasCompleteCache = localStorage.getItem(CACHE_TIMESTAMP_KEY) !== null;

    // Force refresh if cache has too few products (incomplete/stale fallback data)
    const MIN_EXPECTED_PRODUCTS = 5;
    if (!forceRefresh && cachedProducts && cachedProducts.length < MIN_EXPECTED_PRODUCTS) {
        console.log(`Cache has only ${cachedProducts.length} products, forcing refresh...`);
        forceRefresh = true;
    }

    // Check if cache is valid and we have a complete product set
    if (!forceRefresh && cachedProducts && hasCompleteCache && isCacheValid()) {
        console.log('Using cached products (still valid)');
        return cachedProducts;
    }

    // Try loading from localStorage first (only if we have a complete cache)
    if (!forceRefresh && hasCompleteCache && loadCachedProducts()) {
        // Use the cache if it has enough products; otherwise fall through to a fresh fetch
        if (!cachedProducts || cachedProducts.length >= MIN_EXPECTED_PRODUCTS) {
            return cachedProducts;
        }
    }

    isFetching = true;
    productFetchPromise = (async () => {
        try {
            const response = await api.get('/api/get-all-products');

            if (response.data && response.data.products) {
                cachedProducts = response.data.products;

                // Cache in localStorage with timestamp
                localStorage.setItem(CACHE_KEY, JSON.stringify({
                    products: cachedProducts
                }));
                localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

                return cachedProducts;
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error fetching products from server:', error);

            // Fall back to cached data even if expired
            if (loadCachedProducts()) {
                console.log('Using expired cache as fallback');
                return cachedProducts;
            }

            throw error;
        } finally {
            isFetching = false;
            productFetchPromise = null;
        }
    })();
    return productFetchPromise;
};

/**
 * Clear the cache (useful for debugging or forced refresh)
 */
export const clearProductCache = () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    cachedProducts = null;
    console.log('Product cache cleared');
};

export const findProductByUrlName = (urlName) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    // Normalize by replacing underscores/hyphens and lowercasing for flexible matching
    const normalize = (str) => str?.toLowerCase().replace(/[-_\s]+/g, '-').trim();
    const normalizedUrlName = normalize(urlName);
    // Priority Physician Review is a checkout add-on — it must never resolve to a
    // product detail page, even though its url (physician_review) is routable.
    const listable = (cachedProducts || []).filter(product => !isFastTrackProduct(product));
    // Try matching by url field first, then fall back to matching by generated url from name
    return listable.find(product => normalize(product.url) === normalizedUrlName)
        || listable.find(product => normalize(createUrlName(product)) === normalizedUrlName);
};


export const findProductById = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    return findAllInCatalog(cachedProducts, id).map(item => item.form_name);
};


export const getProductByStripeId = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    const product = cachedProducts.filter(item => item.stripe_id === id);
    return product;
}

export const getProductById = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    return findAllInCatalog(cachedProducts, id);
};

/**
 * Async version - fetches from API if product not found in cache
 */
export const getProductByIdAsync = async (id) => {
    // Try cache first
    if (!cachedProducts) {
        loadCachedProducts();
    }
    const product = findAllInCatalog(cachedProducts, id);

    // If found in cache, return it
    if (product.length > 0) {
        return product;
    }

    // Not in cache - fetch from server
    try {
        await fetchProductsFromServer(true); // force refresh
        return findAllInCatalog(cachedProducts, id);
    } catch (error) {
        console.error('Error fetching product:', error);
        return [];
    }
};

export const getProductsByCategory = (category, type = 1) => {
    // Excludes the Priority Physician Review checkout add-on: it carries
    // category.primary "Weight" and is_prescription 1, so every Weight rail and shop
    // grid would otherwise list it for sale as a standalone product.
    return cachedProducts?.filter(product =>
        !isFastTrackProduct(product) &&
        (product.category.primary === category ||
         product.category.secondary === category ||
         product.category.tertiary === category) &&
        product.is_prescription == type
    ) || [];
};

export const getProductsByOffer = (offer) => {
    return cachedProducts?.filter(product => 
        (product.stripe_id === offer)) || [];
};

export const getProductsById = (id) => {
    return findAllInCatalog(cachedProducts, id);
};

export const getProductsByPharmacy = (pharmacy) => {
    return cachedProducts?.filter(product => product.pharmacy === pharmacy) || [];
};

export const getProductsByPriceRange = (minPrice, maxPrice) => {
    return cachedProducts?.filter(product => {
        const price = product.pricing.discounted_price;
        return price >= minPrice && price <= maxPrice;
    }) || [];
};

export const getProductVariants = (baseName) => {
    return cachedProducts?.filter(product => 
        product.name.toLowerCase() === baseName.toLowerCase()
    ) || [];
};

export const hasVariants = (productName) => {
    return (cachedProducts?.filter(product => 
        product.name.toLowerCase() === productName.toLowerCase()
    ) || []).length > 1;
};

export const getUniqueProductNames = () => {
    return [...new Set(cachedProducts?.map(product => product.name) || [])];
};

export const getVariantDetails = (product) => {
    if (!product.variant) return null;
    
    const details = {
        fullName: `${product.name} ${product.variant}`,
        variant: product.variant,
        isShipCold: product.variant.includes('*Ship Cold*'),
        strength: null,
        volume: null,
        form: null
    };

    const strengthMatch = product.variant.match(/(\d+\.?\d*)\s*(mg|mcg|%)/i);
    if (strengthMatch) {
        details.strength = {
            value: parseFloat(strengthMatch[1]),
            unit: strengthMatch[2]
        };
    }

    const volumeMatch = product.variant.match(/(\d+\.?\d*)\s*(ml|mL)/i);
    if (volumeMatch) {
        details.volume = {
            value: parseFloat(volumeMatch[1]),
            unit: volumeMatch[2]
        };
    }

    const forms = ['Cream', 'Tablet', 'Injectable', 'Pump', 'Vial'];
    forms.forEach(form => {
        if (product.variant.includes(form)) {
            details.form = form;
        }
    });

    return details;
};

export const getFullProductName = (product) => {
    return product.variant ? `${product.name} ${product.variant}` : product.name;
};

export const getDisplayName = (product, includeVariant = true) => {
    if (!includeVariant || !product.variant) {
        return product.name;
    }
    
    const cleanVariant = product.variant
        .replace('*Ship Cold*', '')
        .trim();
    
    return `${product.name} ${cleanVariant}`;
};

export const getDiscountedPrice = (product) => {
    return product.pricing.discounted_price;
};

export const getSavingsAmount = (product) => {
    return product.pricing.savings_amount;
};

export const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(price);
};

export const getProductBenefits = (product) => {
    return product.benefits.key_points;
};

export const getProductTestimonials = (product) => {
    return product.testimonials;
};

export const getProductBullets = (product) => {
    return [
        product.product_bullets.bullet1,
        product.product_bullets.bullet2,
        product.product_bullets.bullet3,
        product.product_bullets.bullet4
    ];
};

export const getShippingInfo = (product) => {
    return {
        cost: product.pricing.shipping_cost,
        type: product.pricing.shipping_type,
        additionalCost: product.pricing.additional_cost
    };
};

export const requiresColdShipping = (product) => {
    return product.variant?.includes('*Ship Cold*') || 
           product.pricing.shipping_type === 'Overnight';
};

export const getAllCategories = () => {
    const categories = new Set();
    cachedProducts?.forEach(product => {
        if (product.category.primary) categories.add(product.category.primary);
        if (product.category.secondary) categories.add(product.category.secondary);
        if (product.category.tertiary) categories.add(product.category.tertiary);
    });
    return Array.from(categories);
};

export const sortProductsByPrice = (productList, ascending = true) => {
    return [...(productList || [])].sort((a, b) => {
        const priceA = a.pricing.discounted_price;
        const priceB = b.pricing.discounted_price;
        return ascending ? priceA - priceB : priceB - priceA;
    });
};

export const sortProductVariants = (variants, criteria = 'strength') => {
    return [...(variants || [])].sort((a, b) => {
        const detailsA = getVariantDetails(a);
        const detailsB = getVariantDetails(b);
        
        if (!detailsA || !detailsB) return 0;

        switch (criteria) {
            case 'strength':
                return (detailsA.strength?.value || 0) - (detailsB.strength?.value || 0);
            case 'volume':
                return (detailsA.volume?.value || 0) - (detailsB.volume?.value || 0);
            case 'price':
                return a.pricing.discounted_price - b.pricing.discounted_price;
            default:
                return 0;
        }
    });
};

export const searchProducts = (query) => {
    const searchTerm = query.toLowerCase();
    return cachedProducts?.filter(product =>
        !isFastTrackProduct(product) && (
        product.name.toLowerCase().includes(searchTerm) ||
        product.brand_name.toLowerCase().includes(searchTerm) ||
        product.marketing.main_header.toLowerCase().includes(searchTerm) ||
        product.marketing.sub_header.toLowerCase().includes(searchTerm))
    ) || [];
};

export const getProductWithVariants = (productName) => {
    const variants = getProductVariants(productName);
    const baseProduct = variants[0];
    
    if (!baseProduct) return null;
    
    return {
        name: productName,
        baseProduct: baseProduct,
        variants: sortProductVariants(variants),
        hasMultipleVariants: variants.length > 1,
        lowestPrice: Math.min(...variants.map(v => v.pricing.discounted_price)),
        highestPrice: Math.max(...variants.map(v => v.pricing.discounted_price)),
        forms: [...new Set(variants.map(v => getVariantDetails(v)?.form).filter(Boolean))]
    };
};

export const getProductAnalytics = () => {
    if (!cachedProducts) return null;
    
    const uniqueProducts = getUniqueProductNames();
    return {
        totalProducts: cachedProducts.length,
        uniqueProducts: uniqueProducts.length,
        categoryCounts: getAllCategories().reduce((acc, category) => {
            acc[category] = getProductsByCategory(category).length;
            return acc;
        }, {}),
        pharmacyCounts: [...new Set(cachedProducts.map(p => p.pharmacy))].reduce((acc, pharmacy) => {
            acc[pharmacy] = getProductsByPharmacy(pharmacy).length;
            return acc;
        }, {}),
        priceRange: {
            min: Math.min(...cachedProducts.map(p => p.pricing.discounted_price)),
            max: Math.max(...cachedProducts.map(p => p.pricing.discounted_price)),
            average: cachedProducts.reduce((acc, p) => acc + p.pricing.discounted_price, 0) / cachedProducts.length
        },
        productsWithVariants: uniqueProducts.filter(name => hasVariants(name)).length
    };
};